import type { ReadingSlide } from "../ui/StartReader";
import { Frame, C, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the data-handling start screen. Three short
 * slides (what/who, main risks, key terms) shown in the reader while the 3D room
 * downloads. Content only; the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. classification label set: public / internal / confidential       */
/* ------------------------------------------------------------------ */
interface LabelSpec {
  name: string;
  colour: string;
  example: string;
  rule: string;
  people: number;
}

const LABELS: LabelSpec[] = [
  { name: "PUBLIC", colour: C.mint, example: "Published price list", rule: "Safe to share with anyone", people: 5 },
  { name: "INTERNAL", colour: C.amber, example: "Team rota, meeting notes", rule: "Staff and approved partners only", people: 3 },
  { name: "CONFIDENTIAL", colour: C.red, example: "Customer records, contracts", rule: "Named people only, kept protected", people: 1 },
];

export function LabelsArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: three data labels" aria="Three data labels: public, internal and confidential, each with an example file and who may see it" height={H}>
      {LABELS.map((l, i) => {
        const y = 34 + i * 86;
        const last = i === LABELS.length - 1;
        return (
          <g key={l.name}>
            <rect x="16.5" y={y + 0.5} width="327" height="77" rx="10" fill={C.panel2} stroke={last ? l.colour : C.line} strokeOpacity={last ? 0.5 : 1} />
            <rect
              className={last ? "sr-anim-pulse" : undefined}
              x="30"
              y={y + 12}
              width={last ? 110 : 84}
              height="24"
              rx="12"
              fill={l.colour}
              fillOpacity="0.14"
              stroke={l.colour}
              strokeOpacity="0.5"
            />
            <text x={30 + (last ? 55 : 42)} y={y + 28} fontSize="10.5" fontWeight="700" fill={l.colour} textAnchor="middle" letterSpacing="0.8">
              {l.name}
            </text>
            {Array.from({ length: 5 }, (_, k) => (
              <circle key={k} cx={296 + k * 10 - 20} cy={y + 24} r="3.6" fill={k < l.people ? l.colour : C.line} fillOpacity={k < l.people ? 0.9 : 0.6} />
            ))}
            <text x="30" y={y + 54} fontSize="12.5" fontWeight="700" fill={C.text}>
              {l.example}
            </text>
            <text x="30" y={y + 69} fontSize="10.5" fill={C.muted}>
              {l.rule}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. right place vs wrong place send diagram                          */
/* ------------------------------------------------------------------ */
export function SendArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: where the file goes" aria="A confidential customer list can go to an approved company share, the right place, or to a personal email or chat app, the wrong place" height={H}>
      {/* source file */}
      <rect x="16.5" y="104.5" width="108" height="113" rx="10" fill={C.panel2} stroke={C.line} />
      <path d="M58 120 h22 l8 8 v26 h-30 z" fill="none" stroke={C.soft} strokeWidth="1.5" strokeLinejoin="round" />
      <polyline points="80,120 80,128 88,128" fill="none" stroke={C.soft} strokeWidth="1.5" strokeLinejoin="round" />
      <line x1="64" y1="137" x2="82" y2="137" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <line x1="64" y1="144" x2="78" y2="144" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      <text x="70.5" y="174" fontSize="10" fontFamily={MONO} fill={C.soft} textAnchor="middle">
        customers.xlsx
      </text>
      <rect x="28" y="184" width="85" height="20" rx="10" fill={C.red} fillOpacity="0.14" stroke={C.red} strokeOpacity="0.5" />
      <text x="70.5" y="198" fontSize="9.5" fontWeight="700" fill={C.red} textAnchor="middle" letterSpacing="0.6">
        CONFIDENTIAL
      </text>

      {/* connectors */}
      <path d="M125 161 C152 161 152 98 190 98" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" />
      <polyline points="184,93 190,98 184,103" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M125 161 C152 161 152 228 190 228" fill="none" stroke={C.red} strokeWidth="1.6" strokeLinecap="round" strokeDasharray="4 4" />
      <polyline points="184,223 190,228 184,233" fill="none" stroke={C.red} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />

      {/* right place */}
      <rect x="190.5" y="38.5" width="153" height="119" rx="10" fill={C.mint} fillOpacity="0.07" stroke={C.mint} strokeOpacity="0.45" />
      <text x="204" y="58" fontSize="10" fontWeight="700" fill={C.mint} letterSpacing="0.8">
        RIGHT PLACE
      </text>
      <circle className="sr-anim-pulse" cx="324" cy="54" r="9" fill={C.mint} fillOpacity="0.16" />
      <polyline points="319.5,54 323,57.5 329,50.5" fill="none" stroke={C.mint} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <text x="204" y="82" fontSize="12.5" fontWeight="700" fill={C.text}>
        Approved company share
      </text>
      <text x="204" y="100" fontSize="10.5" fill={C.muted}>
        Named recipients only
      </text>
      <text x="204" y="115" fontSize="10.5" fill={C.muted}>
        Access can be removed
      </text>
      <text x="204" y="130" fontSize="10.5" fill={C.muted}>
        Activity is logged
      </text>

      {/* wrong place */}
      <rect x="190.5" y="168.5" width="153" height="115" rx="10" fill={C.red} fillOpacity="0.07" stroke={C.red} strokeOpacity="0.45" />
      <text x="204" y="188" fontSize="10" fontWeight="700" fill={C.red} letterSpacing="0.8">
        WRONG PLACE
      </text>
      <circle className="sr-anim-ring" cx="324" cy="184" r="9" fill="none" stroke={C.red} strokeWidth="1.5" />
      <circle cx="324" cy="184" r="9" fill={C.red} fillOpacity="0.16" />
      <line x1="320" y1="180" x2="328" y2="188" stroke={C.red} strokeWidth="1.8" strokeLinecap="round" />
      <line x1="328" y1="180" x2="320" y2="188" stroke={C.red} strokeWidth="1.8" strokeLinecap="round" />
      <text x="204" y="212" fontSize="12.5" fontWeight="700" fill={C.text}>
        Personal email or chat
      </text>
      <text x="204" y="230" fontSize="10.5" fill={C.muted}>
        Anyone can forward it
      </text>
      <text x="204" y="245" fontSize="10.5" fill={C.muted}>
        You cannot take it back
      </text>
      <text x="204" y="260" fontSize="10.5" fill={C.muted}>
        No record of who has it
      </text>
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. redaction / least exposure                                       */
/* ------------------------------------------------------------------ */
const ROWS = [
  { id: "#1042", who: "A. Moreau" },
  { id: "#1043", who: "J. Okafor" },
  { id: "#1044", who: "L. Brandt" },
  { id: "#1045", who: "S. Ito" },
];

const PILLS = ["Less data", "Fewer people", "Shorter time"];

export function RedactArt(): JSX.Element {
  const H = 298;
  return (
    <Frame label="Example: share only what is needed" aria="An order table with the card number column blacked out, and three habits: less data, fewer people, shorter time" height={H}>
      <defs>
        <clipPath id="dh-redact-clip">
          <rect x="190" y="62" width="150" height="120" />
        </clipPath>
      </defs>

      <rect x="16.5" y="36.5" width="327" height="150" rx="10" fill={C.panel2} stroke={C.line} />
      <text x="30" y="56" fontSize="10.5" fill={C.muted}>
        Order
      </text>
      <text x="92" y="56" fontSize="10.5" fill={C.muted}>
        Customer
      </text>
      <text x="204" y="56" fontSize="10.5" fill={C.muted}>
        Card number
      </text>
      <line x1="17" y1="64.5" x2="343" y2="64.5" stroke={C.line} />

      {ROWS.map((r, i) => {
        const y = 86 + i * 28;
        return (
          <g key={r.id}>
            <text x="30" y={y} fontSize="11.5" fontFamily={MONO} fill={C.soft}>
              {r.id}
            </text>
            <text x="92" y={y} fontSize="12" fill={C.text}>
              {r.who}
            </text>
            <rect x="204" y={y - 12} width="118" height="16" rx="3" fill={C.code} stroke={C.red} strokeOpacity="0.4" />
            {i < ROWS.length - 1 ? <line x1="17" y1={y + 10.5} x2="343" y2={y + 10.5} stroke={C.line} strokeOpacity="0.6" /> : null}
          </g>
        );
      })}
      <g clipPath="url(#dh-redact-clip)">
        <rect className="sr-anim-sweep" x="190" y="62" width="30" height="120" fill={C.red} fillOpacity="0.14" />
      </g>

      <rect x="16.5" y="198.5" width="327" height="46" rx="8" fill={C.mint} fillOpacity="0.08" stroke={C.mint} strokeOpacity="0.4" />
      <circle cx="35" cy="221.5" r="7" fill={C.mint} fillOpacity="0.16" />
      <polyline points="31.5,221.5 34,224.1 38.5,218.7" fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="50" y="219" fontSize="12" fontWeight="600" fill={C.mint}>
        The recipient needs order status only
      </text>
      <text x="50" y="235" fontSize="10.5" fill={C.muted}>
        Card numbers are removed before it is sent
      </text>

      {PILLS.map((p, i) => {
        const x = 16 + i * 114;
        return (
          <g key={p}>
            <rect x={x + 0.5} y="256.5" width="102" height="28" rx="14" fill={C.panel2} stroke={C.line} />
            <text x={x + 51.5} y="274.5" fontSize="11" fontWeight="600" fill={C.text} textAnchor="middle">
              {p}
            </text>
          </g>
        );
      })}
    </Frame>
  );
}

export const DATA_HANDLING_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Know what you hold. Handle it to match.",
    illustration: LabelsArt,
    caption: "The label decides who may see it and where it may go.",
    sections: [
      {
        h: "What this is about",
        body: "A normal working day in the office. You open, send, store, print and discuss information about customers, colleagues and the business. This course shows you how to recognise what needs protection and handle it safely, from collecting it to disposing of it.",
      },
      {
        h: "Who it\u2019s for",
        body: "Everyone who works with company or customer information. That means all staff, in every team. No technical background needed.",
      },
      {
        h: "Why your judgement matters",
        body: "Systems can lock files and log access. They cannot tell that a spreadsheet with an ordinary name holds customer records, or that a quick message is going to the wrong person. You see the information first. What happens next is your call.",
      },
    ],
  },
  {
    tab: "Main risks",
    headline: "Where everyday handling goes wrong.",
    illustration: SendArt,
    caption: "Same file, same sender. The destination changes the risk.",
    sections: [
      {
        h: "Unlabelled does not mean public",
        body: "Sensitive information often looks ordinary. A plain filename, a printout with no warning, a chat in the corridor. If you are not sure, treat it as protected until you check.",
      },
      {
        h: "Right data, wrong place",
        body: "Personal email, a chat app, an AI tool nobody approved, a printer tray, a shared lounge. Once it lands there, you cannot pull it back or see who else has it.",
      },
      {
        h: "More than the job needs",
        body: "Sending the full file when one column would do. Giving edit rights when read-only would do. Keeping copies after the task is over. Every extra person or copy is another way for it to leak.",
      },
      {
        h: "Hiding a mistake",
        body: "Sending something to the wrong person happens. Staying quiet turns a small slip into a larger incident. Report it quickly so it can be contained.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words this course uses.",
    illustration: RedactArt,
    caption: "Remove what the recipient does not need before you send.",
    sections: [
      {
        h: "Personal information",
        body: "Anything about an identifiable person: contact details, a CV, a performance review.",
      },
      {
        h: "Higher-risk personal information",
        body: "Details that cause real harm if exposed, such as health records, identity documents and financial data.",
      },
      {
        h: "Confidential business information",
        body: "Non-public pricing, contracts, plans, designs and other intellectual property.",
      },
      {
        h: "Classification label",
        body: "A tag such as public, internal or confidential that tells people how to handle the information.",
      },
      {
        h: "Need to know",
        body: "Access goes only to people who need the information to do their job, and only for as long as they need it.",
      },
      {
        h: "Redaction",
        body: "Removing or blacking out the parts a recipient does not need before sharing, so you expose the least.",
      },
    ],
  },
];
